import { ArrowLeftRight, ClipboardList, Grid3x3, Map, Package, PackageSearch, Tags } from 'lucide-react';

import { defineArea } from '../../app/area';
import { RequirePermission } from '../../auth/guards';
import { BinsPage } from './pages/BinsPage';
import { CountingPage } from './pages/CountingPage';
import { LotDetailPage, LotsPage } from './pages/LotsPage';
import { MapPage } from './pages/MapPage';
import { MovementsPage } from './pages/MovementsPage';
import { MovesPage } from './pages/MovesPage';
import { PalletDetailPage } from './pages/PalletDetailPage';
import { PalletsPage } from './pages/PalletsPage';
import { StockPage } from './pages/StockPage';
import { StocktakeDetailPage } from './pages/StocktakeDetailPage';
import { StocktakingPage } from './pages/StocktakingPage';

/**
 * OWNER: the `warehouse-core` module agent (M2 — docs/MODULES.md).
 * Owns: apps/web/src/areas/warehouse-core/**  ·  apps/api/src/modules/warehouse-core/**
 *       packages/shared/src/contracts/warehouse-core.ts
 *       packages/shared/src/i18n/{sv,en,zh}/warehouseCore.json  ·  e2e/warehouse-core.md
 *
 * Two route groups:
 *   `/warehouse/*`  — the admin pages, inside `AdminLayout`.
 *   `/stocktaking`  — the TABLET counting UI. It is a sibling of the admin
 *     pages, not a child: a counter holding a tablet in an aisle needs the whole
 *     screen for 4rem touch targets, with no sidebar and no breadcrumb. Its
 *     entry points are the "Öppna räkneläge" buttons on the stocktaking pages.
 *
 * Every page is also permission-gated server-side; `RequirePermission` here only
 * saves the user a screenful of 403s.
 */
export default defineArea({
  id: 'warehouse-core',
  nav: [
    {
      section: 'warehouse',
      to: '/warehouse/map',
      labelKey: 'common:nav.warehouse.map',
      icon: Map,
      permissions: ['warehouse.map'],
      order: 10,
    },
    {
      section: 'warehouse',
      to: '/warehouse/bins',
      labelKey: 'common:nav.warehouse.bins',
      icon: Grid3x3,
      permissions: ['warehouse.locations'],
      order: 20,
    },
    {
      section: 'warehouse',
      to: '/warehouse/stock',
      labelKey: 'common:nav.warehouse.stock',
      icon: PackageSearch,
      permissions: ['warehouse.stock.read'],
      order: 30,
    },
    {
      section: 'warehouse',
      to: '/warehouse/movements',
      labelKey: 'common:nav.warehouse.movements',
      icon: ArrowLeftRight,
      permissions: ['warehouse.stock.movements'],
      order: 40,
    },
    {
      section: 'warehouse',
      to: '/warehouse/pallets',
      labelKey: 'common:nav.warehouse.pallets',
      icon: Package,
      permissions: ['warehouse.pallets.manage', 'warehouse.stock.read'],
      order: 50,
    },
    {
      section: 'warehouse',
      to: '/warehouse/lots',
      labelKey: 'common:nav.warehouse.lots',
      icon: Tags,
      permissions: ['warehouse.lots.manage', 'warehouse.stock.read'],
      order: 60,
    },
    {
      section: 'warehouse',
      to: '/warehouse/stocktaking',
      labelKey: 'common:nav.warehouse.stocktaking',
      icon: ClipboardList,
      permissions: ['warehouse.inventory.task', 'warehouse.inventory.count'],
      order: 70,
    },
    {
      section: 'warehouse',
      to: '/warehouse/moves',
      labelKey: 'common:nav.warehouse.moves',
      icon: ArrowLeftRight,
      permissions: ['warehouse.goods.move'],
      order: 80,
    },
  ],
  routes: [
    {
      path: 'warehouse/map',
      element: (
        <RequirePermission permissions={['warehouse.map']}>
          <MapPage />
        </RequirePermission>
      ),
    },
    {
      path: 'warehouse/bins',
      element: (
        <RequirePermission permissions={['warehouse.locations', 'warehouse.map']}>
          <BinsPage />
        </RequirePermission>
      ),
    },
    {
      path: 'warehouse/stock',
      element: (
        <RequirePermission permissions={['warehouse.stock.read']}>
          <StockPage />
        </RequirePermission>
      ),
    },
    {
      path: 'warehouse/movements',
      element: (
        <RequirePermission permissions={['warehouse.stock.movements']}>
          <MovementsPage />
        </RequirePermission>
      ),
    },
    {
      path: 'warehouse/pallets',
      element: (
        <RequirePermission permissions={['warehouse.stock.read', 'warehouse.pallets.manage']}>
          <PalletsPage />
        </RequirePermission>
      ),
    },
    {
      path: 'warehouse/pallets/:id',
      element: (
        <RequirePermission permissions={['warehouse.stock.read', 'warehouse.pallets.manage']}>
          <PalletDetailPage />
        </RequirePermission>
      ),
    },
    {
      path: 'warehouse/lots',
      element: (
        <RequirePermission permissions={['warehouse.stock.read', 'warehouse.lots.manage']}>
          <LotsPage />
        </RequirePermission>
      ),
    },
    {
      path: 'warehouse/lots/:id',
      element: (
        <RequirePermission permissions={['warehouse.stock.read', 'warehouse.lots.manage']}>
          <LotDetailPage />
        </RequirePermission>
      ),
    },
    {
      path: 'warehouse/stocktaking',
      element: (
        <RequirePermission permissions={['warehouse.inventory.task', 'warehouse.inventory.count']}>
          <StocktakingPage />
        </RequirePermission>
      ),
    },
    {
      path: 'warehouse/stocktaking/:id',
      element: (
        <RequirePermission permissions={['warehouse.inventory.task', 'warehouse.inventory.count']}>
          <StocktakeDetailPage />
        </RequirePermission>
      ),
    },
    {
      path: 'warehouse/moves',
      element: (
        <RequirePermission permissions={['warehouse.goods.move']}>
          <MovesPage />
        </RequirePermission>
      ),
    },

    // --- the tablet counting UI (full-bleed, no admin chrome) ---------------
    {
      path: 'stocktaking',
      element: (
        <RequirePermission permissions={['warehouse.inventory.count', 'warehouse.inventory.task']}>
          <CountingPage />
        </RequirePermission>
      ),
    },
    {
      path: 'stocktaking/:id',
      element: (
        <RequirePermission permissions={['warehouse.inventory.count', 'warehouse.inventory.task']}>
          <CountingPage />
        </RequirePermission>
      ),
    },
  ],
});
